import React from 'react';
import { enrollmentCodes as ajaxClient } from '../../ajax_clients';
import ActionDropDown from "../shared/action_drop_down";
import pagedTableLayout from '../paged_table/layout';
import { bindMethods, callWithIds, truncate } from "../../shared/functions";
import { Tooltip } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import NoEncryptionIcon from '@mui/icons-material/NoEncryption';
import HomeIcon from '@mui/icons-material/Home';
import DevicesIcon from '@mui/icons-material/Devices';
import DvrIcon from '@mui/icons-material/Dvr';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import DnsIcon from '@mui/icons-material/Dns';

const getAjaxClient = () => {
    return {
        ...ajaxClient,
        destroyMultiple: (enrollmentCodes) => callWithIds(enrollmentCodes, ajaxClient.destroyMultiple),
        enableRemoteDesktop: (enrollmentCodes) => callWithIds(enrollmentCodes, ajaxClient.enableRemoteDesktop),
        disableRemoteDesktop: (enrollmentCodes) => callWithIds(enrollmentCodes, ajaxClient.disableRemoteDesktop)
    }
};

class EnrollmentCodesPagedTable extends React.Component {

    constructor(props) {
        super(props);
        bindMethods(this);
    }

    renderFeatureIcon(feature) {
       switch (feature) {
            case('Welcome Screen'):
                return <Tooltip describeChild arrow key='home' title={feature}><HomeIcon fontSize='small' /></Tooltip>;
            case('Authentication'):
                return <Tooltip describeChild arrow key='lock' title={feature}><LockIcon fontSize='small' /></Tooltip>;
            case('No Authentication'):
                return <Tooltip describeChild arrow key='no-auth' title={feature}><NoEncryptionIcon fontSize='small' /></Tooltip>;
            case('User Enrollment'):
                return <Tooltip describeChild arrow key='user' title={feature}><PersonIcon fontSize='small' /></Tooltip>;
            case('Enable Remote Desktop'):
                return <Tooltip describeChild arrow key='remote-desktop' title={feature}><DvrIcon fontSize='small' /></Tooltip>;
            case('Account Driven'):
                return <Tooltip describeChild arrow key='account-driven' title={feature}><DevicesIcon fontSize='small' /></Tooltip>;
            case('Service Discovery'):
                return <Tooltip describeChild arrow key='service-discovery' title={ feature }><TravelExploreIcon fontSize="small" /></Tooltip>;
            case('External server configuration'):
                return <Tooltip describeChild arrow key='external-server' title={ feature }><DnsIcon fontSize="small" /></Tooltip>;
            default:
        }
    }

    enableRemoteDesktop(e) {
        e.preventDefault()

        const confirmMessage = "Enable the Apple Remote Desktop service on macOS 10.14.4+ at the time of enrollment. Devices that are already enrolled will not be affected. Are you sure?"

        if (confirm(confirmMessage)) {
            this.props.callWithItems('enableRemoteDesktop');
        }
    }

    disableRemoteDesktop(e) {
        e.preventDefault()

        const confirmMessage = "Do not enable the Apple Remote Desktop service on macOS 10.14.4+ at the time of enrollment. Devices that are already enrolled will not be affected. Are you sure?"

        if (confirm(confirmMessage)) {
            this.props.callWithItems('disableRemoteDesktop');
        }
    }

    renderActionButton() {
        if (!this.props.policy.edit) {
            return null;
        }
        const items = [
            {
                href:   "#delete",
                action: this.props.removeItems,
                text:   "Delete",
                policy: 'destroyMultiple',
            },
            {
                href:   "#enable_remote_desktop",
                action: this.enableRemoteDesktop,
                text:   "Enable Remote Desktop",
                policy: 'update',
            },
            {
                href:   "#disable_remote_desktop",
                action: this.disableRemoteDesktop,
                text:   "Disable Remote Desktop",
                policy: 'update',
            },
        ];
        return <ActionDropDown text="Actions" items={ items } policy={ this.props.policy }
                               disabled={ !this.props.hasCheckedItems() }/>
    }

    renderAlertMessage({ alertMessage }) {
        if (alertMessage) {
            const { level, message } = alertMessage;
            return (
                <span className={ `label label-${ level } label-rejected` }>
                    <span className="glyphicon glyphicon-alert"/>
                    &nbsp;
                    { message }
                </span>
            )
        }
    }

    renderOneTimeLink({ device }) {
        if (device.name && device.id) {
            return (
                <React.Fragment>
                    <Tooltip title="One Time Enrollment" describeChild arrow>
                        <PhoneIphoneIcon fontSize="small" />
                    </Tooltip>
                    <span>
                        (<a href={ AdminRoutes.adminDevicePath(device.id, {format: null}) }>{ device.name }</a>)
                    </span>
                </React.Fragment>
            )
        }
    }

    pagedTableProps() {
        return {
            headers:   [
                { displayName: "Name", key: "name" },
                { displayName: "Type", key: "type" },
                { displayName: "Features", key: "features", classNames: ['expand'] },
            ],
            renderRow: (row) => {
                const name = <a href={ AdminRoutes.adminEnrollmentCodePath(row.id, {format: null}) }>{ truncate(row.name, 50) }</a>;
                const type = <React.Fragment>
                    { row.enrollmentType }
                    { this.renderAlertMessage(row) }
                </React.Fragment>;

                const features = <div className="features-wrapper">
                    { row.features.map(this.renderFeatureIcon) }
                    { this.renderOneTimeLink(row) }
                </div>;

                return { ...row, name, type, features }
            }
        };
    }

    renderAddEnrollmentCodeButton() {
        if (this.props.policy.create) {
            const items = [
                { href: AdminRoutes.newAdminDEPAccountsPath({format: null}), text: "Automated Enrollment (DEP)" },
                { href: AdminRoutes.newAdminEnrollmentCodePath({format: null}), text: "Group Enrollment" },
                { href: AdminRoutes.newAdminDevicePath({format: null}), text: "One-Time Enrollment" },
            ];
            return <ActionDropDown text="Add Enrollment" items={ items }/>;
        }
    }

    render() {
        const notFound = (
            <div className="centered alert alert-info" role="alert">
                No enrollments codes found.
            </div>
        );

        const message = <p>Enrollments provide a means of adding devices to your SimpleMDM account.
            Enrollment codes can be a link your users visit from their devices. They can also link your account to
            Automated Enrollment within Apple Business Manager.</p>;
        const actions = (
            <div className="button-list-container">
                { this.renderAddEnrollmentCodeButton() }
                { this.renderActionButton() }
            </div>
        );
        return (
            <div id="enrollment-codes-table">
                { this.props.renderTopSection(message, actions) }
                { this.props.dataNotFound ? notFound : this.props.renderPagedTable(this.pagedTableProps()) }
            </div>
        )
    }

}

export default pagedTableLayout(EnrollmentCodesPagedTable, getAjaxClient);